import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';

// Register Swiper custom elements
import { register as registerSwiperElements } from 'swiper/element/bundle';
registerSwiperElements();
import { IMAGE_CONFIG, registerLocaleData } from '@angular/common';

import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import {
  ApiModule as AuthorityApiModule,
  Configuration as AuthorityConfiguration,
} from '@tfagency/authority/data-access';
import { ApiModule as CoreApiModule, Configuration as CoreConfiguration } from '@tfagency/core/data-access';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import {
  ErrorService,
  LoadingInterceptor,
  locales,
  ServerHttpInterceptor,
  StorageKeyConstants,
} from '@tfagency/shared/util';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from './app/shared/services/custom-mat-paginator-intl.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { routes } from './app/app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';

registerLocaleData(localeSv);
registerLocaleData(localeEn);

if (environment.sentryDSN) {
  Sentry.init({
    dsn: environment.sentryDSN,
    release: 'tfagency@2.0.0',
    dist: '1',
    environment: environment.sentryEnvironment,
    integrations: [
      // Add browser profiling integration to the list of integrations
      Sentry.browserProfilingIntegration(),
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.browserTracingIntegration(),
      // Registers the Replay integration,
      // which automatically captures Session Replays
      Sentry.replayIntegration(),
    ],
    /* integrations: [
      // Performance Monitoring
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          'localhost',
          environment.coreApiUrl,
          environment.authorityApiUrl,
        ],
      }),
      // Session Replay
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ], */
    tracesSampleRate: 1.0, // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring. We recommend adjusting this value in production.
    replaysSessionSampleRate: environment.production ? 0.1 : 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      CoreApiModule.forRoot(
        () =>
          new CoreConfiguration({
            basePath: environment.coreApiUrl,
            withCredentials: false,
          }),
      ),
      AuthorityApiModule.forRoot(
        () =>
          new AuthorityConfiguration({
            basePath: environment.authorityApiUrl,
            withCredentials: true,
          }),
      ),
      OAuthModule.forRoot({
        resourceServer: {
          allowedUrls: [environment.coreApiUrl],
          sendAccessToken: true,
        },
      }),
      TranslateModule.forRoot({
        defaultLanguage:
          (localStorage.getItem(StorageKeyConstants.LANGUAGE_CODE) as string) != null
            ? (localStorage.getItem(StorageKeyConstants.LANGUAGE_CODE) as string)
            : locales.defaultLanguge.code,
        loader: provideTranslateHttpLoader(),
      }),
      MatDatepickerModule,
      MatNativeDateModule,
    ),

    provideCharts(withDefaultRegisterables()),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      // withPreloading(PreloadAllModules),
      withComponentInputBinding(),
    ),
    provideZoneChangeDetection({
      eventCoalescing: true,
    }),
    provideAnimationsAsync(),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_CARD_CONFIG,
      useValue: { appearance: 'outlined' },
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerHttpInterceptor,
      multi: true,
      deps: [ErrorService, OAuthService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
  ],
});
